import { useEffect, useState } from "react";
import MultiStepper from "../../../../common/MultiStepper";
import AccordionComponent from "../../../../common/Accordion";
import SearchableListSelect from "../../../../common/SearchableListSelect";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import DataTable from "../../../../common/DataTable";
import { MAPPING_SUMMARY_HEADERS } from "../action/constant";
import {
  Button,
  Col,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import OverlayDrawer from "../../../../common/OverlayDrawer";
import { GENERAL_LOG_HEADERS } from "../../../Case_Management/Case_Information/actions/Constant";
import {
  addUserMappingDetails,
  getUserMappingDetails,
  getUserMappingLimitedRoles,
  getUserMappingUserListByRoleID,
  updateUserMappingDetails,
} from "../../../../services/TeamsUserMapping";
import Loader from "../../../../common/Loader";
import Snackbar from "../../../../common/Snackbar";
import moment from "moment";
import MultilineTextBox from "../../../../common/MultilineTextBox";
import GroupingDropdown from "../../../../common/GroupingDropdown";

const AddUpdateUserMapping: React.FC<any> = ({ teamId, userData, onClose }) => {
  const stepsData = [
    { label: "Primary Role", icon: "security" },
    { label: "Select User", icon: "account_circle" },
    { label: "Role Mapping", icon: "manage_accounts" },
    { label: "User Mapping", icon: "lock_person" },
  ];

  const loggedInUserid = useSelector((state: RootState) => state.auth.userid);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [activeKeySummary, setActiveKeySummary] = useState<string | null>("0");
  const [selectedRole, setSelectedRole] = useState<(string | number)[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<(string | number)[]>([]);
  const [selectedRoleMappings, setSelectedRoleMappings] = useState<
    (string | number)[]
  >([]);
  const [selectedUsers, setSelectedUsers] = useState<(string | number)[]>([]);
  const [primaryRoleOptions, setPrimaryRoleOptions] = useState<any[]>([]);
  const [doctorOptions, setDoctorOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [roleMappingOptions, setRoleMappingOptions] = useState<any[]>([]);
  const [logsData, setLogsData] = useState<any[]>([]);
  const [showModalNew, setShowModalNew] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [comments, setComments] = useState<string>("");
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const [scrollToRole, setScrollToRole] = useState<string | null>(null);

  const stringToNumberArray = (val?: string): number[] => {
    if (!val) return [];
    return val?.split(",").map((v) => Number(v.trim()));
  };

  const fetchUserMappingRolesData = async () => {
    try {
      setLoading(true);
      const webRoleData = await getUserMappingLimitedRoles();
      setPrimaryRoleOptions(webRoleData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserMappingUserListByRoleID = async (
    Id: (string | number)[],
    setOptions: React.Dispatch<React.SetStateAction<any>>,
    useNested = false,
  ) => {
    const params = {
      teamId,
      roleIds: Id.join(","),
      userIdException: loggedInUserid,
    };

    try {
      const roleList = await getUserMappingUserListByRoleID(params);

      if (useNested) {
        const groupedData = roleList.reduce((acc: any, item: any) => {
          const groupName = item.userGroupName;
          const roleName = item.userRoleNames;
          if (!acc[groupName]) acc[groupName] = {};
          if (!acc[groupName][roleName]) acc[groupName][roleName] = [];
          acc[groupName][roleName].push({ label: item.label, value: item.value });
          return acc;
        }, {});

        const options = Object.keys(groupedData).map((group) => ({
          group,
          subGroups: Object.keys(groupedData[group]).map((subGroup) => ({
            subGroup,
            options: groupedData[group][subGroup],
          })),
        }));
        setOptions(options || []);
      } else {
        const groupedData = roleList.reduce((acc: any, item: any) => {
          const groupName = item.userGroupName;
          if (!acc[groupName]) acc[groupName] = [];
          acc[groupName].push({ label: item.label, value: item.value });
          return acc;
        }, {});

        const options = Object.keys(groupedData).map((group) => ({
          group,
          options: groupedData[group],
        }));
        setOptions(options || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchUserMappingDetailsData = async () => {
    try {
      setLoading(true);

      const details = await getUserMappingDetails(userData?.teamUserMappingId);
      if (!details?.teamUserMappingId) return;

      const {
        primaryRoleId: roleId,
        primaryRoleUserId: doctorId,
        roleMappingIds,
        roleMappingUserIds,
        createdDate,
        modifiedDate,
        createdByName,
        modifiedByName,
      } = details;

      const roleMappingIdsArr = stringToNumberArray(roleMappingIds);
      const userIdsArr = stringToNumberArray(roleMappingUserIds);

      setSelectedRole([roleId]);
      setSelectedDoctor([doctorId]);
      setSelectedRoleMappings(roleMappingIdsArr);
      setSelectedUsers(userIdsArr);
      setComments(details?.comments || "");
      setActiveStep(3);

      const [roleOptionData] = await Promise.all([
        getUserMappingLimitedRoles(),
        fetchUserMappingUserListByRoleID([roleId], setDoctorOptions),
        roleMappingIdsArr?.length
          ? fetchUserMappingUserListByRoleID(roleMappingIdsArr, setUserOptions, true)
          : Promise.resolve(),
      ]);

      const filteredRoles = roleOptionData.filter(
        (role: any) => Number(role.value) !== Number(roleId),
      );

      setRoleMappingOptions(filteredRoles);
      setLogsData([
        {
          createdDate: moment(createdDate).format("MM/DD/YYYY | hh:mm A"),
          modifiedDate: moment(modifiedDate).format("MM/DD/YYYY | hh:mm A"),
          createdByName,
          modifiedByName,
        },
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserMappingRolesData();
  }, []);

  useEffect(() => {
    if (userData?.teamUserMappingId) {
      fetchUserMappingDetailsData();
    }
  }, [userData?.teamUserMappingId]);

  const getStepStatus = (index: number) => {
    if (index < activeStep) return "Completed";
    if (index === activeStep) return "In Progress";
    return "Pending";
  };

  const isAllSelected =
    selectedRole?.length > 0 &&
    selectedDoctor?.length > 0 &&
    selectedRoleMappings?.length > 0 &&
    selectedUsers?.length > 0;

  const isStepDisabled = (index: number) => {
    if (isAllSelected) return false;

    return index > activeStep;
  };

  const getTitleWithCount = (
    title: string,
    count: number,
    selectedLabels: string[],
    uniqueKey: string,
  ) => (
    <div className="d-flex justify-content-between w-100 position-relative">
      <span>{title}</span>

      {count > 0 && (
        <div
          className="badge-wrapper"
          onMouseEnter={() => setHoveredBadge(uniqueKey)}
          onMouseLeave={() => setHoveredBadge(null)}
        >
          <span className="ms-2 primary-badge badge bg-primary">{count}</span>

          {hoveredBadge === uniqueKey && (
            <div className="custom-hover-box">
              <div className="custom-hover-boxinner">
                {selectedLabels.length ? (
                  selectedLabels.map((label, index) => (
                    <div key={index} className="hover-item">
                      {label}
                    </div>
                  ))
                ) : (
                  <div className="hover-item">No data</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAccordion = (
    title: React.ReactNode,
    content: JSX.Element,
    stepIndex: number,
  ) => {
    return (
      <AccordionComponent
        items={[{ title, content }]}
        activeKey={activeStep === stepIndex ? "0" : "0"}
        showArrow={false}
        isUserMapping={true}
      />
    );
  };

  const handleRoleChange = (val: (string | number)[]) => {
    setSelectedRole(val);
    setSelectedDoctor([]);
    setSelectedRoleMappings([]);
    setSelectedUsers([]);
    setUserOptions([]);
    fetchUserMappingUserListByRoleID(val, setDoctorOptions);
    const filteredRoles = primaryRoleOptions.filter(
      (role: any) => !val.includes(role.value),
    );

    setRoleMappingOptions(filteredRoles);
    if (val?.length > 0) setActiveStep(1);
  };

  const handleDoctorChange = (val: (string | number)[]) => {
    setSelectedDoctor(val);
    setSelectedUsers([]);
    setUserOptions([]);
    if (val?.length > 0) setActiveStep(2);
  };

  const handleRoleMappingChange = async (val: (string | number)[]) => {
    // Detect newly added role to auto-scroll User Mapping to that sub-group
    if (val.length > selectedRoleMappings.length) {
      const prevSet = new Set(selectedRoleMappings.map(String));
      const newlyAdded = val.find((v) => !prevSet.has(String(v)));
      if (newlyAdded) {
        setScrollToRole(String(getLabel(roleMappingOptions, newlyAdded)));
      }
    } else {
      setScrollToRole(null);
    }

    setSelectedRoleMappings(val);

    if (!val || val?.length === 0) {
      setUserOptions([]);
      setSelectedUsers([]);
      setActiveStep(2);
      return;
    }
    
    try {
      const params = {
        teamId,
        roleIds: val.join(","),
        userIdException: loggedInUserid,
      };

      const roleList = await getUserMappingUserListByRoleID(params);

      const groupedData = roleList.reduce((acc: any, item: any) => {
        const groupName = item.userGroupName;
        const roleName = item.userRoleNames;

        if (!acc[groupName]) acc[groupName] = {};
        if (!acc[groupName][roleName]) acc[groupName][roleName] = [];

        acc[groupName][roleName].push({ label: item.label, value: item.value });
        return acc;
      }, {});

      const options = Object.keys(groupedData).map((group) => ({
        group,
        subGroups: Object.keys(groupedData[group]).map((subGroup) => ({
          subGroup,
          options: groupedData[group][subGroup],
        })),
      }));

      setUserOptions(options || []);

      const flatOptions = options.flatMap((opt: any) =>
        opt.subGroups
          ? opt.subGroups.flatMap((sub: any) => sub.options)
          : opt.options ?? [opt],
      );

      const validUserIds = new Set(flatOptions.map((o: any) => o.value));

      setSelectedUsers((prev) =>
        prev.filter((userId) => validUserIds.has(userId)),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    if (val?.length > 0) {
      setActiveStep(3);
    } else {
      setActiveStep(2);
    }
  };

  const handleUserChange = (val: (string | number)[]) => {
    setSelectedUsers(val);
  };

  const getLabel = (options: any[], value: string | number) => {
    const flatOptions = options.flatMap((opt) => {
      if (opt.subGroups) return opt.subGroups.flatMap((sub: any) => sub.options);
      return opt.options ? opt.options : opt;
    });
    return flatOptions.find((o: any) => o.value === value)?.label || value;
  };

  const summaryRows = [
    {
      primaryRole: selectedRole?.map((v) => getLabel(primaryRoleOptions, v)).join(", ") || "-",
      qmeDoctor: selectedDoctor?.map((v) => getLabel(doctorOptions, v)).join(", ") || "-",
      roleMapping: selectedRoleMappings?.map((v) => getLabel(roleMappingOptions, v)).join(", ") || "-",
      userMapping: selectedUsers?.map((v) => getLabel(userOptions, v)).join(", ") || "-",
    },
  ];

  const validateData = () => {
    const validationErrors: string[] = [];

    if (!selectedRole || selectedRole?.length === 0) {
      validationErrors.push("Please select Primary Role");
    }

    if (!selectedDoctor || selectedDoctor?.length === 0) {
      validationErrors.push(`Please select ${primaryRoleLabel}`);
    }

    if (!selectedRoleMappings || selectedRoleMappings?.length === 0) {
      validationErrors.push("Please select Role Mapping");
    }

    if (!selectedUsers || selectedUsers?.length === 0) {
      validationErrors.push("Please select User Mapping");
    }

    if (validationErrors?.length > 0) {
      setSnackbarMessage(validationErrors.join("\n"));
      setSnackbarOpen(true);
      setError("error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateData()) return;
    const payload = {
      teamId: teamId,
      primaryRoleId: Number(selectedRole?.[0]) || 0,
      primaryRoleUserId: Number(selectedDoctor?.[0]) || 0,
      roleMappingIds: selectedRoleMappings?.length
        ? selectedRoleMappings.join(",")
        : "",
      roleMappingUserIds: selectedUsers?.length ? selectedUsers.join(",") : "",
      comments: comments || "",
    };

    const updatePayload = {
      ...payload,
      teamUserMappingId: userData?.teamUserMappingId,
      isActive: userData?.isActive,
    };
    try {
      setLoading(true);
      let response;
      if (userData?.teamUserMappingId) {
        response = await updateUserMappingDetails(updatePayload);
      } else {
        response = await addUserMappingDetails(payload);
      }
      if (response?.isSuccess) {
        setSnackbarMessage(
          userData?.teamUserMappingId
            ? "Team User Mapping updated successfully."
            : "Team User Mapping Added successfully.",
        );
        setError("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setSnackbarMessage(
          response?.responseMessage ||
            response?.message ||
            "Failed to process data.",
        );
        setError("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error processing data:", error);
      setSnackbarMessage("An error occurred while processing the data.");
      setSnackbarOpen(true);
      setError("error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelButton = () => {
    setShowModalNew(true);
  };

  const handleCloseModalNew = () => {
    setShowModalNew(false);
  };

  const resetFields = () => {
    setSelectedRole([]);
    setSelectedDoctor([]);
    setSelectedRoleMappings([]);
    setSelectedUsers([]);
    setDoctorOptions([]);
    setUserOptions([]);
    setRoleMappingOptions(primaryRoleOptions || []);
    setActiveStep(0);
  };

  const handleConfirmCancelButton = async () => {
    setShowModalNew(false);
    if (userData?.teamUserMappingId) {
      fetchUserMappingDetailsData();
    } else {
      resetFields();
    }
  };

  const handleOpenDrawer = () => {
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleBack = () => {
    onClose();
  };

  const primaryRoleLabel =
    selectedRole?.length > 0
      ? getLabel(primaryRoleOptions, selectedRole[0])
      : "Primary User";

  const getDisabledStyle = (stepIndex: number): React.CSSProperties => {
    if (userData?.teamUserMappingId && (stepIndex === 0 || stepIndex === 1)) {
      return { pointerEvents: "none", opacity: 0.5 };
    }

    if (isStepDisabled(stepIndex)) {
      return { pointerEvents: "none", opacity: 0.5 };
    }

    return {};
  };

  const reorderWithSelected = (
    options: any[] = [],
    selectedValues: any[] = [],
  ) => {
    if (!selectedValues?.length) return options;
    const set = new Set(selectedValues);

    if (options?.length && options[0]?.subGroups) {
      return options
        .map((group: any) => {
          const reorderedSubs = group.subGroups
            .map((sub: any) => ({
              ...sub,
              options: [
                ...sub.options.filter((o: any) => set.has(o.value)),
                ...sub.options.filter((o: any) => !set.has(o.value)),
              ],
              _hasSelected: sub.options.some((o: any) => set.has(o.value)),
            }))
            .sort((a: any, b: any) => Number(b._hasSelected) - Number(a._hasSelected))
            .map(({ _hasSelected, ...rest }: any) => rest);

          const groupHasSelected = group.subGroups.some((sub: any) =>
            sub.options.some((o: any) => set.has(o.value)),
          );
          return { ...group, subGroups: reorderedSubs, _hasSelected: groupHasSelected };
        })
        .sort((a: any, b: any) => Number(b._hasSelected) - Number(a._hasSelected))
        .map(({ _hasSelected, ...rest }: any) => rest);
    }

    if (options?.length && options[0]?.options) {
      return options
        .map((group: any) => {
          const sortedOptions = [
            ...group.options.filter((o: any) => set.has(o.value)),
            ...group.options.filter((o: any) => !set.has(o.value)),
          ];
          const hasSelected = group.options.some((o: any) => set.has(o.value));
          return { ...group, options: sortedOptions, hasSelected };
        })
        .sort((a: any, b: any) => Number(b.hasSelected) - Number(a.hasSelected))
        .map(({ hasSelected, ...rest }) => rest);
    }

    return [
      ...options?.filter((o: any) => set.has(o.value)),
      ...options?.filter((o: any) => !set.has(o.value)),
    ];
  };

  return (
    <>
      <Loader isLoading={loading} />
      <Snackbar
        message={snackbarMessage}
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={error}
      />
      <div className="mt-3 mb-3">
        <div className="container-fluid px-5">
          <MultiStepper
            steps={stepsData.map((step, index) => ({
              ...step,
              status: getStepStatus(index),
            }))}
          />

          <div className="row mt-1 equal-height-row">
            <div className="selection_dropdown col-md-3 d-flex mb-3">
              <div
                className="w-100 h-100 mappingboxer"
                style={getDisabledStyle(0)}
              >
                {renderAccordion(
                  "Primary Role",
                  <SearchableListSelect
                    key="primary-role-select"
                    options={
                      userData?.teamUserMappingId
                        ? reorderWithSelected(primaryRoleOptions, selectedRole)
                        : primaryRoleOptions
                    }
                    values={selectedRole}
                    onChange={handleRoleChange}
                    isMultiple={false}
                    placeholder={"Search Role"}
                  />,
                  0,
                )}
              </div>
            </div>

            <div className="selection_dropdown col-md-3 d-flex mb-3">
              <div
                className="w-100 h-100 mappingboxer"
                style={getDisabledStyle(1)}
              >
                {renderAccordion(
                  `${primaryRoleLabel}`,
                  <SearchableListSelect
                    key="doctor-select"
                    options={
                      userData?.teamUserMappingId
                        ? reorderWithSelected(doctorOptions, selectedDoctor)
                        : doctorOptions
                    }
                    values={selectedDoctor}
                    onChange={handleDoctorChange}
                    isMultiple={false}
                    placeholder={"Search User"}
                  />,
                  1,
                )}
              </div>
            </div>

            <div className="selection_dropdown col-md-3 d-flex mb-3">
              <div
                className="w-100 h-100 mappingboxer"
                style={getDisabledStyle(2)}
              >
                {renderAccordion(
                  getTitleWithCount(
                    "Role Mapping",
                    selectedRoleMappings?.length,
                    selectedRoleMappings?.map((v) =>
                      getLabel(roleMappingOptions, v),
                    ),
                    "roleMapping",
                  ),
                  <SearchableListSelect
                    key="role-mapping-select"
                    options={roleMappingOptions}
                    values={selectedRoleMappings}
                    onChange={handleRoleMappingChange}
                    isMultiple={true}
                    placeholder={"Search Role"}
                  />,
                  2,
                )}
              </div>
            </div>

            <div className="selection_dropdown col-md-3 d-flex mb-3">
              <div
                className="w-100 h-100 mappingboxer"
                style={getDisabledStyle(3)}
              >
                {renderAccordion(
                  getTitleWithCount(
                    "User Mapping",
                    selectedUsers?.length,
                    selectedUsers?.map((v) => getLabel(userOptions, v)),
                    "userMapping",
                  ),
                  <GroupingDropdown
                    key="user-select"
                    options={userOptions}
                    values={selectedUsers}
                    onChange={handleUserChange}
                    isMultiple={true}
                    singleSelectPerSubGroup={true}
                    placeholder={"Search User"}
                    nestedGroup={true}
                    scrollToSubGroup={scrollToRole}
                  />,
                  3,
                )}
              </div>
            </div>
            {selectedRole?.length > 0 && (
              <div className="mappingSummaryGroup">
                <AccordionComponent
                  items={[
                    {
                      title: "Mapping Summary",
                      content: (
                        <>
                          <DataTable
                            headers={MAPPING_SUMMARY_HEADERS}
                            rows={summaryRows}
                            isUserMappingTable={true}
                          />
                        </>
                      ),
                    },
                  ]}
                  activeKey={activeKeySummary}
                  onSelect={(key) => setActiveKeySummary(key as string)}
                />
              </div>
            )}
            <div className="mt-1">
              <MultilineTextBox
                label="Comment"
                name="comment"
                value={comments}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value.startsWith(" ")) return;
                  value = value?.trimStart();
                  value = value.replace(/ {2,}/g, " ");
                  setComments(value);
                }}
                className="sharerecodemultiline"
                maxLength={100}
              />
            </div>
          </div>
          <Row className="mb-5 mt-2">
            <Col className="d-flex justify-content-start">
              <>
                <Button
                  variant="outline"
                  className="d-flex align-items-center btn-text-border me-1"
                  onClick={handleBack}
                >
                  <span className="material-icons mr-2 ">arrow_back</span>
                  Back
                </Button>

                {userData?.teamUserMappingId && (
                  <OverlayTrigger
                    placement="auto"
                    overlay={
                      <Tooltip style={{ backgroundColor: "#b3e0ff" }}>
                        Logs
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center mx-2 me-0"
                      onClick={handleOpenDrawer}
                    >
                      <span className="material-icons">description</span>
                    </button>
                  </OverlayTrigger>
                )}
              </>
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                variant="outline-info"
                className="d-flex align-items-center m-0"
                onClick={handleCancelButton}
              >
                <span className="material-icons mr-2">close</span> Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="d-flex align-items-center mx-2"
                onClick={handleSubmit}
              >
                <span className="material-icons mx-1">save</span> Save
              </Button>
            </Col>
          </Row>
        </div>
        <OverlayDrawer
          title={"Logs"}
          open={drawerOpen}
          onClose={handleCloseDrawer}
          placement="bottom"
        >
          <DataTable
            headers={GENERAL_LOG_HEADERS}
            rows={logsData}
            isLogTable={true}
          />
        </OverlayDrawer>

        <Modal show={showModalNew} onHide={handleCloseModalNew}>
          <Modal.Body className="fw-bold text-left modelheading ">
            <div>Are you sure you want to cancel?</div>
          </Modal.Body>
          <Modal.Footer className="border-top d-flex justify-content-end">
            <Button
              variant="outline-info"
              className="d-flex align-items-end"
              onClick={handleCloseModalNew}
            >
              <span className="material-icons mr-2">close</span> Cancel
            </Button>
            <Button
              onClick={handleConfirmCancelButton}
              variant="primary"
              className="ms-3 d-flex align-items-center"
            >
              <span className="material-icons mx-1">check</span> Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default AddUpdateUserMapping;
